// @ts-check
import { COUNTRIES, countryByIso, countryForPhone } from '../../utils/countries';
import { deadlineBadge, formatDay, label, STAGE_BADGE } from '../../utils/wholesale';
import CountrySelect from '../CountrySelect';

export const input = 'w-full px-3 py-2 text-base border border-input rounded-lg bg-card';
export const primaryBtn =
  'px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:bg-muted-foreground/50';
export const secondaryBtn = 'px-4 py-2 text-sm border border-input rounded-lg hover:bg-accent disabled:opacity-50';
export const card = 'bg-card border rounded-lg border-border shadow-sm';

/** @param {{label: string, hint?: string, children: React.ReactNode, className?: string}} props */
export const Field = ({ label: text, hint, children, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium mb-1">{text}</label>
    {children}
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

/** @param {{stage: string}} props */
export const StageBadge = ({ stage }) => (
  <span
    className={`inline-flex px-2.5 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${STAGE_BADGE[stage] || ''}`}
  >
    {label(stage)}
  </span>
);

/** Apply-by / event countdown for a partner; renders nothing when it has no dates. */
export const DeadlineBadge = ({ item }) => {
  const badge = deadlineBadge(item);
  return badge ? (
    <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${badge.className}`}>
      {badge.text}
    </span>
  ) : null;
};

const NEVER_EMAIL = { hard_bounced: 'Bounced', complained: 'Spam complaint' };

/** Red badge for a partner whose first email bounced or drew a spam complaint; nothing otherwise. */
export const DeliveryBadge = ({ delivery }) =>
  delivery && NEVER_EMAIL[delivery.status] ? (
    <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300">
      {NEVER_EMAIL[delivery.status]}
    </span>
  ) : null;

/**
 * What Postmark told us about the latest outreach email (first message or follow-up): a red alert for a hard bounce or complaint (the address is on the
 * do-not-send list), a subtle line for a soft bounce or a delivery, nothing when there is no result.
 */
export const DeliveryNote = ({ delivery }) => {
  if (!delivery) return null;
  const day = formatDay(delivery.at);
  if (NEVER_EMAIL[delivery.status]) {
    const optedOut = delivery.detail === 'Unsubscribed';
    const lead =
      delivery.status !== 'complained'
        ? `Email bounced on ${day}`
        : optedOut
          ? `Opted out on ${day}`
          : `Spam complaint on ${day}`;
    const advice =
      delivery.status === 'complained'
        ? 'The address is on the do-not-send list; do not email it again. Add another contact and reach them another way, then log it on the timeline (or Mark as sent) to restart the follow-up clock.'
        : 'The address is on the do-not-send list. Add another contact and email them by hand, then log it on the timeline (or Mark as sent) to restart the follow-up clock.';
    return (
      <p
        role="alert"
        className="lg:col-span-3 text-sm px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
      >
        {lead}
        {delivery.detail ? `: ${delivery.detail}` : ''}. {advice}
      </p>
    );
  }
  if (delivery.status === 'soft_bounced' || delivery.status === 'delivered') {
    return (
      <p className="lg:col-span-3 text-xs text-muted-foreground">
        {delivery.status === 'delivered' ? 'Delivered' : 'Soft bounce'} {day}
      </p>
    );
  }
  return null;
};

/** A <select> over a list of enum values. */
export const EnumSelect = ({ value, onChange, options, allowEmpty = false, emptyLabel = '', ...rest }) => (
  <select className={input} value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...rest}>
    {allowEmpty && <option value="">{emptyLabel}</option>}
    {options.map((o) => (
      <option key={o} value={o}>
        {label(o)}
      </option>
    ))}
  </select>
);

const DEFAULT_COUNTRY = 'IN';

/**
 * A phone number with its country: the dial-code selector always sits beside the number field. The
 * value is one string like "+91 9082764700" (empty when no number), so it stores as plain text.
 * @param {{value: string, onChange: (value: string) => void, placeholder?: string, ariaLabel?: string}} props
 */
export const PhoneField = ({ value, onChange, placeholder = 'Number', ariaLabel = 'Phone' }) => {
  const country = countryForPhone(value, DEFAULT_COUNTRY) || countryByIso(DEFAULT_COUNTRY) || COUNTRIES[0];
  const national = value && value.startsWith('+') ? value.slice(country.dial.length).trim() : value || '';
  const compose = (iso, number) => {
    const c = countryByIso(iso) || country;
    return number.trim() ? `${c.dial} ${number.trim()}` : '';
  };
  return (
    <div className="flex gap-2">
      <CountrySelect
        variant="phone"
        ariaLabel={`${ariaLabel} country`}
        value={country.iso}
        onChange={(iso) => onChange(compose(iso, national))}
      />
      <input
        className={`${input} min-w-0`}
        type="tel"
        inputMode="tel"
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={national}
        onChange={(e) => onChange(compose(country.iso, e.target.value))}
      />
    </div>
  );
};

/** Opens a CSV/ZIP blob as a download. */
export const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
